import React, { useEffect, useState, useCallback } from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Typography,
  Card,
  Tooltip,
  Grid,
  FormControl,
  Autocomplete,
  TextField,
} from "@mui/material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftTypography from "components/SoftTypography";
import { AxiosError } from "axios";

const getMuiTheme = () =>
  createTheme({
    components: {},
  });

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};
interface EmployeeFilter {
  employee_id: number;
  name: string;
}

// Move renderer functions OUTSIDE the component
const renderEmployee = (value: string | null) => (
  <span>{value ?? "Unassigned"}</span>
);

const renderAddress = (value: string, tableMeta: any) => {
  const row = tableMeta?.tableData?.[tableMeta.rowIndex];
  const rowObj =
    row && !Array.isArray(row) && typeof row === "object" ? row : null;

  // Always start from the rendered cell value so the table isn't blank.
  const addressParts = [
    value,
    (rowObj as any)?.address_type,
    (rowObj as any)?.region_name,
    (rowObj as any)?.city_name,
    (rowObj as any)?.state_name,
  ]
    .map((v: unknown) => (v == null ? "" : String(v).trim()))
    .filter((v: string) => v.length > 0 && v.toLowerCase() !== "undefined");
  const address = addressParts.length ? addressParts.join(", ") : "—";

  return (
    <Tooltip title={address}>
      <span>
        {address.length > 20 ? `${address.substring(0, 20)}...` : address}
      </span>
    </Tooltip>
  );
};

const EmployeeClosedTickets = () => {
  const [rows, setRows] = useState([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [employees, setEmployees] = React.useState<EmployeeFilter[]>([]);
  const [employeeFilter, setEmployeeFilter] =
    React.useState<EmployeeFilter | null>(null);
  const fetchTickets = useCallback(async () => {
    try {
      const response = await axios.get(`/tickets`, {
        params: {
          status_id: 7,
          filters: {
            employee_id: employeeFilter?.employee_id,
          },
        },
      });
      setRows(response.data.list);
    } catch (error) {
      if (error instanceof AxiosError && error) {
        setError(error);
      }
      console.error("Error fetching tickets:", error);
    }
  }, [employeeFilter]);

  useEffect(() => {
    fetchTickets();
  }, [employeeFilter, fetchTickets]);
  const fetchEmployees = async () => {
    try {
      const employeeData = await axios.get("/employee");
      setEmployees(employeeData?.data?.list);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  React.useEffect(() => {
    fetchEmployees();
  }, []);
  const columns: MUIDataTableColumn[] = [
    {
      name: "employee_name",
      label: "Employee",
      options: {
        customBodyRender: renderEmployee,
      },
    },
    { name: "ticket_service_id", label: "Service ID" },
    { name: "customer_name", label: "Customer" },
    { name: "customer_phone", label: "Phone" },
    {
      name: "address",
      label: "Address",
      options: {
        filter: false,
        customBodyRender: renderAddress,
      },
    },
    { name: "description", label: "Description" },
    { name: "category_name", label: "Category" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "20px auto", maxWidth: "2000px" }}
      >
        <Typography variant="h4" gutterBottom>
          Closed Tickets
        </Typography>
        <div
          style={{
            display: "block",
            justifyContent: "flex-end",
            float: "right",
          }}
        >
          <Grid style={{ float: "left" }}>
            <FormControl sx={{ paddingRight: "10px" }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "10px" }}
              >
                Employee
              </SoftTypography>
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => option.name || ""}
                value={employeeFilter}
                onChange={(event, value) => setEmployeeFilter(value)}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </FormControl>
          </Grid>
        </div>

        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title={"Closed Tickets"}
            data={rows}
            columns={columns}
            options={tableOptions}
          />
        </ThemeProvider>

        {error && <div style={{ color: "red" }}>Error: {error.message}</div>}
      </Card>
    </DashboardLayout>
  );
};

export default EmployeeClosedTickets;
